export function distinct<T>(item: T, index: number, array: T[]) {
  return array.indexOf(item) === index
}

export function distinctAndTruthy<T>(
  item: T,
  index: number,
  array: T[],
): item is NonNullable<T> {
  return Boolean(item) && distinct(item, index, array)
}

/**
 * Finds the next or previous valid element in an array based on the current element.
 *
 * @param elements - The array of elements to search through.
 * @param current - The current element to find the adjacent element for.
 * @param direction - The direction to search: "next" or "previous".
 * @param isValid - A function to determine if an element is valid. Defaults to checking if the element is not undefined.
 * @returns The next or previous valid element, or undefined if no valid element is found.
 */
export function findAdjacent<T>(
  elements: T[],
  current: T | undefined,
  direction: "next" | "previous",
  isValid: (element: T | undefined) => boolean = t => t !== undefined,
) {
  if (current === undefined || elements.length <= 1) {
    return undefined
  }

  const currentIndex = elements.indexOf(current)

  if (currentIndex === -1) return undefined

  const inc = direction === "next" ? 1 : -1

  const stopCondition = (i: number) => direction === "next" ? i < elements.length : i >= 0

  for (let i = currentIndex + inc; stopCondition(i); i += inc) {
    const element = elements[i]
    if (isValid(element)) {
      return element
    }
  }

  return undefined
}
